import { doctorBffApi } from '@/modules';
import {
  NotificationPayload,
  NotificationPerUserParams,
  NotificationService
} from './notification.service';
import { Notification } from './notification.model';
import { wrapError } from '../../error';
import { WithPatientId } from '@/modules/surgicalEvent';
import type { RootState } from '@/store';
import { hospitalApi, HospitalService } from '@/modules/hospital';
import type { BaseQueryApi } from '@reduxjs/toolkit/dist/query/react';

export const notificationApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    userNotifications: builder.query<
      Notification[],
      WithPatientId<NotificationPerUserParams>
    >({
      queryFn: (params, api) =>
        notificationModuleEnabledOnCurrentHospitalOrThrow(api)
          .then(() => NotificationService.getInstance().fetchMessages(params))
          .catch(wrapError),
      providesTags: (result, error, params) => [
        { type: 'Notification', id: params.patientId }
      ]
    }),

    sendNotification: builder.mutation<void, NotificationPayload>({
      queryFn: (payload, api) =>
        notificationModuleEnabledOnCurrentHospitalOrThrow(api)
          .then(() => NotificationService.getInstance().send(payload))
          .catch(wrapError),
      invalidatesTags: (result, error, { toUserId }) => [
        { type: 'Notification', id: toUserId }
      ]
    }),

    retryNotification: builder.mutation<void, Notification>({
      queryFn: (notification, api) =>
        notificationModuleEnabledOnCurrentHospitalOrThrow(api)
          .then(() =>
            NotificationService.getInstance().retryFailed(notification)
          )
          .catch(wrapError),
      invalidatesTags: (result, error, { toUserId }) => [
        { type: 'Notification', id: toUserId }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  useUserNotificationsQuery,
  useSendNotificationMutation,
  useRetryNotificationMutation
} = notificationApi;

async function notificationModuleEnabledOnCurrentHospitalOrThrow(
  api: BaseQueryApi
) {
  const hospitalId = HospitalService.getInstance().getHospitalIdFromJwt();
  const hospital = hospitalApi.endpoints.hospitalById.select(hospitalId)(
    api.getState() as RootState
  )?.data;
  if (hospital?.notificationModuleEnabled === false) {
    throw new Error('Notification module is disabled for this hospital');
  }
}
